import React, { useEffect, useState, useRef } from 'react';

import styles from './styles.module.css';

const getSafeNeedleAngle = (currentKw: number, maxKw: number) => {
  const safeCurrentKw =
    typeof currentKw === 'number' && !isNaN(currentKw) ? currentKw : 0;
  const safeMaxKw =
    typeof maxKw === 'number' && !isNaN(maxKw) && maxKw > 0 ? maxKw : 1;

  const normalizedValue = Math.min(Math.max(safeCurrentKw, 0), safeMaxKw);

  const START_ANGLE = 0;
  const END_ANGLE = 180;
  const SWEEP = END_ANGLE - START_ANGLE;

  const ratio = normalizedValue / safeMaxKw;

  const safeNeedleAngle = START_ANGLE + ratio * SWEEP;

  return safeNeedleAngle;
};

const formatNumber = (num: number): string => {
  if (
    typeof num !== 'number' ||
    isNaN(num) ||
    num === null ||
    num === undefined
  ) {
    return '0';
  }
  if (num % 1 !== 0) {
    return parseFloat(num.toFixed(1)).toLocaleString();
  }
  return num.toLocaleString();
};
interface GaugeProps {
  title?: string;
  subtitle?: string;
  mainLabel?: string;
  currentKw: number;
  maxKw: number;
  powerStatus: string;
  metrics?: {
    ngp: {
      value: number;
      unit: string;
    };
    t5: {
      value: number;
      unit?: string;
    };
    load: {
      value: number;
      unit: string;
    };
  };
  variant?: 'ok' | 'warning' | 'shutdown' | 'offline';
  size?: 'large' | 'small';
  onClick?: () => void;
}

/**
 * Renders a turbine gauge card with power metrics.
 * Offline cards keep offline styling and still display last known metric values.
 * @param {GaugeProps} props - Gauge display props.
 * @returns {JSX.Element | undefined} Large or small gauge markup.
 */
const Gauge: React.FC<GaugeProps> = ({
  size = 'large',
  title,
  subtitle,
  mainLabel,
  currentKw,
  maxKw,
  powerStatus,
  metrics = {
    ngp: { value: 0, unit: '' },
    t5: { value: 0 },
    load: { value: 0, unit: '' }
  },
  variant = 'ok',
  onClick
}) => {
  const needleRef = useRef<SVGGElement>(null);
  const [initialRender, setInitialRender] = useState(true);
  const [currentValue, setCurrentValue] = useState(0);

  const safeNeedleAngle = getSafeNeedleAngle(currentValue, maxKw);

  useEffect(() => {
    if (initialRender) {
      setTimeout(() => {
        setCurrentValue(currentKw);
      }, 1000);

      setCurrentValue(0);
      setInitialRender(false);
      return;
    }
    setCurrentValue(currentKw);
  }, [currentKw]);

  if (size === 'large') {
    return (
      <div
        className={styles.gaugeContainer}
        data-color={variant}
        onClick={onClick}
        style={onClick ? { cursor: 'pointer' } : undefined}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.gaugeSection}>
          <div className={styles.gaugeWrapper}>
            <svg
              width="189"
              height="79"
              viewBox="0 0 189 79"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M187.675 76.8081C183.997 58.6072 175.045 41.7343 161.736 28.4083C143.92 10.5697 119.756 0.548066 94.5601 0.548066C69.3643 0.548066 45.2005 10.5697 27.3843 28.4083C14.0751 41.7343 5.12273 58.6072 1.44509 76.8081H40.762C43.5645 68.7945 48.1443 61.4305 54.2546 55.3125C64.9443 44.6093 79.4426 38.5964 94.5601 38.5964C109.678 38.5964 124.176 44.6094 134.866 55.3125C140.976 61.4305 145.556 68.7945 148.358 76.8081H187.675Z"
                className={styles.gaugeBackgroundPath}
              />
              <path
                d="M94.5607 0.548096V73.641"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M28.3829 28.8206L94.5607 75.6435"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M2.55984 75.6435L93.8514 75.8674"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M96.5608 75.6435L187.852 75.8674"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M161.322 28.832L94.5607 75.6435"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
            </svg>

            <div className={styles.gaugeNeedle}>
              <svg width="200" height="100" viewBox="0 0 100 1">
                <g
                  ref={needleRef}
                  style={{
                    transform: `rotate(${safeNeedleAngle}deg)`,
                    transformOrigin: '92% 60%',
                    transformBox: 'fill-box',
                    transition: 'transform 0.4s ease-out'
                  }}
                >
                  <path
                    className={styles.gaugeNeedlePath}
                    d="M48.4687 8.87085C37.4416 8.87085 12.4687 12.8712 12.4687 12.8712C12.4687 12.8712 37.4416 16.8708 48.4688 16.8708C50.6765 16.8708 52.4665 15.0823 52.4687 12.875M52.4687 12.8712C52.4687 12.8711 52.4687 12.871 52.4687 12.8708C52.4687 10.6617 50.6779 8.87085 48.4687 8.87085"
                  />
                </g>
              </svg>
            </div>
          </div>

          <div className={styles.mainValue}>
            <span className={styles.value}>{formatNumber(currentValue)}</span>
            <span className={styles.label}>{mainLabel}</span>
          </div>
        </div>

        <div className={styles.metricsSection}>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>NGP</span>
            <span className={styles.metricValue}>
              {formatNumber(metrics.ngp.value)}
              {metrics.ngp.unit}
            </span>
          </div>

          <div className={styles.metric}>
            <span className={styles.metricLabel}>T5</span>
            <span className={styles.metricValue}>
              {formatNumber(metrics.t5.value)}
              {metrics.t5.unit || ''}
            </span>
          </div>

          <div className={styles.metric}>
            <span className={styles.metricLabel}>Load</span>
            <div className={styles.loadContainer}>
              <span className={styles.metricValue}>
                {formatNumber(metrics.load.value)}
                {metrics.load.unit}
              </span>
            </div>
            <div className={styles.loadChart}>
              {Array.from({ length: 4 }, (_, index) => {
                const minThreshold = (index / 4) * 100;
                const isActive = metrics.load.value > minThreshold;
                const isFirst = index === 0;
                const isLast = index === 3;

                let barClass = `${styles.loadBar} ${isActive ? styles.loadBarActive : ''}`;
                if (isFirst) barClass += ` ${styles.loadBarBottom}`;
                if (isLast) barClass += ` ${styles.loadBarTop}`;

                return <div key={index} className={barClass} />;
              })}
            </div>
          </div>
        </div>
        <div className={styles.powerStatus}>
          <span className={styles.powerLabel}>{powerStatus}</span>
        </div>
      </div>
    );
  }

  if (size === 'small') {
    return (
      <div
        className={styles.smallGaugeContainer}
        onClick={onClick}
        style={onClick ? { cursor: 'pointer' } : undefined}
      >
        <div className={styles.smallGaugeSection}>
          <div className={styles.gaugeWrapper}>
            <svg
              width="189"
              height="79"
              viewBox="0 0 189 79"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M187.675 76.8081C183.997 58.6072 175.045 41.7343 161.736 28.4083C143.92 10.5697 119.756 0.548066 94.5601 0.548066C69.3643 0.548066 45.2005 10.5697 27.3843 28.4083C14.0751 41.7343 5.12273 58.6072 1.44509 76.8081H40.762C43.5645 68.7945 48.1443 61.4305 54.2546 55.3125C64.9443 44.6093 79.4426 38.5964 94.5601 38.5964C109.678 38.5964 124.176 44.6094 134.866 55.3125C140.976 61.4305 145.556 68.7945 148.358 76.8081H187.675Z"
                className={styles.gaugeBackgroundPath}
              />
              <path
                d="M94.5607 0.548096V73.641"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M28.3829 28.8206L94.5607 75.6435"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M2.55984 75.6435L93.8514 75.8674"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M96.5608 75.6435L187.852 75.8674"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
              <path
                d="M161.322 28.832L94.5607 75.6435"
                stroke="white"
                strokeOpacity="0.362156"
                strokeWidth="1.46488"
              />
            </svg>

            <div className={styles.gaugeNeedle}>
              <svg width="200" height="0" viewBox="0 3 100 1">
                <g
                  ref={needleRef}
                  style={{
                    transform: `rotate(${safeNeedleAngle}deg)`,
                    transformOrigin: '90% 60%',
                    transformBox: 'fill-box',
                    transition: 'transform 0.4s ease-out'
                  }}
                >
                  <path
                    className={styles.gaugeNeedlePath}
                    d="M48.4687 8.87085C37.4416 8.87085 12.4687 12.8712 12.4687 12.8712C12.4687 12.8712 37.4416 16.8708 48.4688 16.8708C50.6765 16.8708 52.4665 15.0823 52.4687 12.875M52.4687 12.8712C52.4687 12.8711 52.4687 12.871 52.4687 12.8708C52.4687 10.6617 50.6779 8.87085 48.4687 8.87085"
                    transform="translate(0, 30)"
                  />
                </g>
              </svg>
            </div>
            <div className={styles.gaugeMinMax}>
              <span className={styles.minValue}>0</span>
              <span className={styles.maxValue}>{formatNumber(maxKw)}</span>
            </div>
          </div>

          <div className={styles.mainValue}>
            <span className={styles.value}>{formatNumber(currentValue)}</span>
            <span className={styles.label}>{mainLabel}</span>
          </div>
        </div>
      </div>
    );
  }
};

export default Gauge;
